import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { ArrowLeft, ArrowRight, CheckCircle2, MapPin, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";
import { SCAN_LANES, SCAN_RESULTS } from "@/content/site";

type Step = 1 | 2 | 3;

const stepOneSchema = z.object({
  city: z.string().trim().min(2, "City required").max(80),
  state: z.string().trim().min(2, "State required").max(40),
  zip: z.string().trim().min(3, "Zip required").max(12),
  program_lane: z.enum(
    SCAN_LANES.map((l) => l.value) as [string, ...string[]],
  ),
});

const stepTwoSchema = z.object({
  full_name: z.string().trim().min(2, "Name required").max(100),
  email: z.string().trim().email("Valid email required").max(255),
  organization: z.string().trim().max(150).optional().or(z.literal("")),
  notes: z.string().trim().max(1000).optional().or(z.literal("")),
});

const inputCls =
  "bg-navy-elevated/60 border-border text-white placeholder:text-silver/50 focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:border-gold/60 transition-colors";

const Scan = () => {
  const [step, setStep] = useState<Step>(1);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  const [form, setForm] = useState({
    city: "",
    state: "",
    zip: "",
    program_lane: "",
    full_name: "",
    email: "",
    organization: "",
    notes: "",
  });

  const update = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleStepOne = () => {
    const r = stepOneSchema.safeParse(form);
    if (!r.success) {
      toast.error(r.error.issues[0]?.message ?? "Please complete the form");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStepTwo = async () => {
    const r = stepTwoSchema.safeParse(form);
    if (!r.success) {
      toast.error(r.error.issues[0]?.message ?? "Please complete the form");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase
      .from("scan_submissions")
      .insert({
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        program_lane: form.program_lane,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        notes: form.notes.trim() || null,
      });
    setSubmitting(false);
    if (error) {
      console.error("scan_submissions insert error:", error);
      toast.error("Something went wrong. Please try again.");
      return;
    }
    setSavedId(null);
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const result = form.program_lane ? SCAN_RESULTS[form.program_lane] : null;
  const laneLabel = SCAN_LANES.find((l) => l.value === form.program_lane)?.label;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Scans · Opps-Finder — DrSullySpeaks"
        description="The Scans Opps-Finder maps your leadership, ministry, or consulting experience into a fundable, AI-enabled program lane in minutes."
        path="/scan"
      />
      <SiteNav />

      <section className="relative pt-32 pb-12 md:pt-40 bg-hero overflow-hidden">
        <div className="container max-w-3xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto">DrSullySpeaks · Scans · Opps-Finder</span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-4 leading-[1.05]">
              {step === 3 ? (
                <>Your <span className="text-gradient-gold">pathway.</span></>
              ) : (
                <>Scans <span className="text-gradient-gold">Opps-Finder.</span><br /><span className="text-white">Find the program inside your expertise.</span></>
              )}
            </h1>
            {step !== 3 && (
              <p className="text-silver text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed">
                A short diagnostic. Two minutes. We'll point you to a program lane
                you can actually fund and build.
              </p>
            )}
          </Reveal>

          {step !== 3 && (
            <div className="mt-10 flex items-center justify-center gap-3 text-xs uppercase tracking-[0.25em]">
              <Dot active={step >= 1} done={step > 1} label="Locate" />
              <span className={cn("h-px w-10 transition-colors", step > 1 ? "bg-babyblue" : "bg-border")} />
              <Dot active={step >= 2} done={step > 2} label="Verify" />
              <span className="h-px w-10 bg-border" />
              <Dot active={false} done={false} label="Pathway" />
            </div>
          )}
        </div>
      </section>

      <section className="pb-32">
        <div className="container max-w-3xl">
          <Reveal variant="scale" className="premium-card p-6 md:p-10">
            {step === 1 && (
              <div className="space-y-8">
                <Sub title="Where are you operating?" hint="City, state, and zip help us calibrate funder and partner pathways." icon={MapPin} />
                <div className="grid md:grid-cols-3 gap-5">
                  <Field label="City *" id="city">
                    <Input id="city" className={inputCls} value={form.city} onChange={update("city")} maxLength={80} placeholder="San Antonio" />
                  </Field>
                  <Field label="State *" id="state">
                    <Input id="state" className={inputCls} value={form.state} onChange={update("state")} maxLength={40} placeholder="TX" />
                  </Field>
                  <Field label="Zip *" id="zip">
                    <Input id="zip" className={inputCls} value={form.zip} onChange={update("zip")} maxLength={12} placeholder="78201" />
                  </Field>
                </div>

                <div className="gold-divider opacity-30" />

                <Sub title="Pick your program lane" hint="Where does your expertise want to land?" icon={Sparkles} />
                <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
                  {SCAN_LANES.map((l) => (
                    <button
                      key={l.value}
                      type="button"
                      onClick={() => setForm((s) => ({ ...s, program_lane: l.value }))}
                      className={cn(
                        "text-left p-5 transition-all",
                        form.program_lane === l.value
                          ? "bg-navy-elevated border-l-2 border-orange"
                          : "bg-navy hover:bg-navy-elevated border-l-2 border-transparent",
                      )}
                    >
                      <div
                        className={cn(
                          "text-xs uppercase tracking-[0.25em]",
                          form.program_lane === l.value ? "text-orange" : "text-gold",
                        )}
                      >
                        {l.label}
                      </div>
                    </button>
                  ))}
                </div>

                <Button
                  onClick={handleStepOne}
                  className="w-full bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] py-6 shadow-cta transition-all"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <Sub title="Where should we send your pathway?" hint="One email — used only to deliver your scan results and (optionally) follow up." icon={Sparkles} />

                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Full Name *" id="full_name">
                    <Input id="full_name" className={inputCls} value={form.full_name} onChange={update("full_name")} maxLength={100} />
                  </Field>
                  <Field label="Email *" id="email">
                    <Input id="email" type="email" className={inputCls} value={form.email} onChange={update("email")} maxLength={255} />
                  </Field>
                </div>

                <Field label="Organization" id="organization">
                  <Input id="organization" className={inputCls} value={form.organization} onChange={update("organization")} maxLength={150} placeholder="Church, school, nonprofit, business..." />
                </Field>

                <Field label="Anything specific you want addressed?" id="notes">
                  <Textarea id="notes" rows={4} className={inputCls} value={form.notes} onChange={update("notes")} maxLength={1000} placeholder="Optional context — what you're building, who you serve." />
                </Field>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="border-gold/40 text-white hover:bg-gold/10 hover:text-white">
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                  <Button
                    onClick={handleStepTwo}
                    disabled={submitting}
                    className="flex-1 bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] py-6 shadow-cta transition-all"
                  >
                    {submitting ? "Generating..." : "Show My Pathway"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && result && (
              <div className="space-y-8">
                <div className="text-center">
                  <div className="relative inline-flex">
                    <div className="absolute inset-0 bg-babyblue/30 blur-2xl rounded-full" />
                    <CheckCircle2 className="relative h-14 w-14 text-babyblue mx-auto" />
                  </div>
                  <div className="text-xs uppercase tracking-[0.3em] text-gold mt-6">
                    {laneLabel} · {form.city}, {form.state}
                  </div>
                  <h2 className="font-display uppercase text-white text-2xl md:text-4xl mt-3 leading-tight">
                    {result.headline}
                  </h2>
                </div>

                <p className="text-silver text-base leading-relaxed text-center max-w-xl mx-auto">
                  {result.pathway}
                </p>

                <div className="border border-gold/30 bg-gold/[0.03] p-6">
                  <div className="text-xs uppercase tracking-[0.3em] text-gold">
                    What it could include
                  </div>
                  <ul className="mt-5 space-y-3">
                    {result.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-white/85 text-sm leading-relaxed">
                        <span className="text-orange mt-2 h-px w-3 bg-orange flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Link to="/inquire?lane=consulting" className="flex-1">
                    <Button className="w-full bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] py-6 shadow-cta">
                      Book a Strategy Call
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                  <Link to="/" className="flex-1">
                    <Button variant="outline" className="w-full border-gold/40 text-white hover:bg-gold/10 hover:text-white font-display uppercase tracking-[0.2em] py-6">
                      Back to Home
                    </Button>
                  </Link>
                </div>

                {savedId && (
                  <p className="text-[10px] uppercase tracking-widest text-silver/50 text-center">
                    Reference: {savedId.slice(0, 8)}
                  </p>
                )}
              </div>
            )}
          </Reveal>

          {step !== 3 && (
            <div className="mt-8 text-center">
              <Link to="/" className="text-silver text-xs uppercase tracking-[0.25em] hover:text-white inline-flex items-center gap-2">
                <ArrowLeft className="h-3 w-3" /> Back to home
              </Link>
            </div>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="container max-w-5xl">
          <div className="text-center mb-10">
            <span className="eyebrow !mx-auto">Keep Exploring</span>
            <h2 className="font-display uppercase text-white text-2xl md:text-3xl mt-3">
              The full <span className="text-gradient-gold">DrSullySpeaks</span> ecosystem
            </h2>
            <p className="text-silver text-sm mt-3 max-w-xl mx-auto">
              Shop the merch, listen to the podcast, and tune into the music — all from drsullyspeaks.com.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
            {[
              { label: "Merch", title: "DrSullySpeaks Merch", desc: "Official apparel & gear via Say So Society.", href: "https://saysosociety.com/collections/drsullyspeaks-merch" },
              { label: "Shop", title: "Say So Society — Global Co.", desc: "Browse the full Say So collections.", href: "https://saysosociety.com/collections" },
              { label: "Stan Store", title: "Books & Digital Drops", desc: "Courses, ebooks, and digital products.", href: "https://stan.store/DrSullySpeaks" },
              { label: "Podcast", title: "Honest Kingdom Voice", desc: "Listen on Spotify.", href: "https://open.spotify.com/show/6t5G1oDtkligs6wi4iGgvZ" },
              { label: "Music", title: "@DrSullySpeaks", desc: "On YouTube Music — and every major platform under @DrSullySpeaks.", href: "https://music.youtube.com/channel/UClW-Fv3VHRzaB5YQtkyhuGA" },
              { label: "Ministry", title: "Anchored SA", desc: "The San Antonio Community Church.", href: "https://www.anchoredsa.com" },
            ].map((c) => (
              <a
                key={c.title}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-navy hover:bg-navy-elevated p-6 transition-colors border-l-2 border-transparent hover:border-orange"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{c.label}</div>
                <div className="font-display uppercase text-white mt-3 text-lg">{c.title}</div>
                <p className="text-silver text-sm mt-2 leading-relaxed">{c.desc}</p>
                <div className="text-xs uppercase tracking-[0.25em] text-orange mt-4 inline-flex items-center gap-2">
                  Visit <ArrowRight className="h-3 w-3" />
                </div>
              </a>
            ))}
          </div>
          <p className="text-center text-xs uppercase tracking-[0.25em] text-silver/60 mt-8">
            drsullyspeaks.com — the home of every offer
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

const Sub = ({
  title,
  hint,
  icon: Icon,
}: {
  title: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
}) => (
  <div>
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">
      <Icon className="h-3.5 w-3.5" /> {title}
    </div>
    {hint && <p className="text-silver text-sm mt-2">{hint}</p>}
  </div>
);

const Field = ({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}) => (
  <div>
    <Label htmlFor={id} className="text-xs uppercase tracking-[0.25em] text-gold">
      {label}
    </Label>
    <div className="mt-2">{children}</div>
  </div>
);

const Dot = ({ active, done, label }: { active: boolean; done: boolean; label: string }) => (
  <div className="flex items-center gap-2">
    <span
      className={cn(
        "h-2.5 w-2.5 rounded-full transition-colors",
        done ? "bg-babyblue shadow-[0_0_12px_hsl(var(--baby-blue)/0.6)]" : active ? "bg-orange shadow-[0_0_12px_hsl(var(--orange)/0.6)]" : "bg-border",
      )}
    />
    <span className={cn(active || done ? "text-white" : "text-silver")}>{label}</span>
  </div>
);

export default Scan;
